import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import "../styles/MyProfile.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CATEGORY_ORDER = ["reward", "challenges", "specialists", "squad"];
const CATEGORY_ID_BY_KEY = {
  reward: 0,
  challenges: 1,
  specialists: 2,
  squad: 3,
};

const TEAM_KEYS = ["puncheur", "tt", "baroudeur", "sprinteur", "flandrien", "stages"];

const MyProfile = ({ emblems, generalCyclists }) => {
  const { t } = useTranslation();

  const emblemes = require.context("../../medias/embleme", false, /\.png$/);
  const images = Array.from({ length: 129 }, (_, index) => emblemes(`./${index + 1}.png`));

  const emblemes1 = require.context("../../medias/embleme1", false, /\.png$/);
  const images1 = Array.from({ length: 129 }, (_, index) => emblemes1(`./${index + 1}.png`));

  const [view, setView] = useState("both");
  const [fullscreenImage, setFullscreenImage] = useState(null);

  const apiUrl =
    process.env.REACT_APP_API_URL || "http://10.0.2.2:6268";

  const [rarityLoading, setRarityLoading] = useState(false);
  const [rarityError, setRarityError] = useState(null);
  const [rarityPct, setRarityPct] = useState(0);

  const abortRef = useRef(null);

  const fetchRarity = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setRarityLoading(true);
    setRarityError(null);

    try {
      const res = await fetch(`${apiUrl}/rarety`, {
        credentials: "include",
        signal: ac.signal,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const v = data?.avgRarityPct;
      const pct =
        typeof v === "number" ? v : typeof v === "string" ? parseFloat(v) : 0;

      setRarityPct(Number.isFinite(pct) ? pct : 0);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setRarityError(e);
      setRarityPct(0);
    } finally {
      setRarityLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchRarity();
    return () => abortRef.current?.abort?.();
  }, [fetchRarity]);

  const handleImageClick = (src) => setFullscreenImage(src);
  const closeFullscreen = (e) => {
    e?.stopPropagation?.();
    setFullscreenImage(null);
  };

  const emblemsSafe = emblems || [];
  const cyclistsSafe = generalCyclists || [];

  const categorizedEmblems = useMemo(() => {
    const byKey = {};
    CATEGORY_ORDER.forEach((k) => (byKey[k] = []));

    for (const emblem of emblemsSafe) {
      const categoryKey = CATEGORY_ORDER.find((k) => CATEGORY_ID_BY_KEY[k] === emblem.category);
      if (categoryKey) byKey[categoryKey].push(emblem);
    }

    return byKey;
  }, [emblemsSafe]);

  const teamCounts = useMemo(() => {
    const out = Object.fromEntries(TEAM_KEYS.map((k) => [k, 0]));

    const stripAccents = (s) =>
      String(s ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

    const norm = (s) =>
      stripAccents(s)
        .toLowerCase()
        .trim()
        .replaceAll("’", "'")
        .replace(/[\s_]+/g, "-");

    const mapToKey = (raw) => {
      const s = norm(raw);

      if (!s) return null;

      if (s.includes("punch")) return "puncheur";
      if (s.includes("sprin")) return "sprinteur";
      if (s.includes("baroud")) return "baroudeur";
      if (s.includes("flandr")) return "flandrien";

      if (s.includes("contre") || s.includes("clm") || s.includes("time-trial") || s === "tt")
        return "tt";

      if (s.includes("etape") || s.includes("stage")) return "stages";

      if (s.includes("classic")) return "flandrien";
      if (s.includes("polyval")) return "puncheur";

      return null;
    };

    for (const c of cyclistsSafe) {
      const raw = c?.specialite ?? c?.speciality ?? c?.specialty ?? c?.role ?? c?.type;
      if (!raw) continue;

      const key = mapToKey(raw);
      if (!key) continue;

      out[key] = (out[key] || 0) + 1;
    }

    return out;
  }, [cyclistsSafe]);

  const chartData = useMemo(() => {
    const labels = TEAM_KEYS.map((k) => t(`myProfile.specialities.${k}`));
    const values = TEAM_KEYS.map((k) => teamCounts[k] || 0);

    return {
      labels,
      datasets: [
        {
          label: t("myProfile.chart.datasetLabel"),
          data: values,
          backgroundColor: ["#0075FC", "#A71AFF", "#D81010", "#20FF18", "#000000", "#FFF70D"],
          borderRadius: 6,
          barThickness: 14,
        },
      ],
    };
  }, [teamCounts, t]);

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y",
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
        title: { display: false },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: { precision: 0 },
          title: {
            display: true,
            text: t("myProfile.axis.numberOfCyclists"),
          },
          grid: { color: "rgba(0,0,0,0.10)" },
        },
        y: {
          title: {
            display: true,
            text: t("myProfile.axis.speciality"),
          },
          grid: { display: false },
        },
      },
    };
  }, [t]);

  const showStats = view !== "left";

  return (
    <div className="my-profiles">
      {fullscreenImage && (
        <div className="my-profiles-fullscreen-container" onClick={closeFullscreen}>
          <img
            src={fullscreenImage}
            alt={t("myProfile.emblems.fullscreenAlt")}
            className="my-profiles-fullscreen-image"
          />
          <div className="my-profiles-close-button" onClick={closeFullscreen}>
            ✖
          </div>
        </div>
      )}

      <div
        className={`my-profiles-left ${view === "left" ? "expanded" : ""} ${
          view === "right" ? "hidden" : ""
        }`}
      >
        <h2>
          {t("myProfile.emblems.title")}{" "}
          <span
            role="button"
            tabIndex={0}
            onClick={() => setView(view === "left" ? "both" : "left")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setView(view === "left" ? "both" : "left");
            }}
            aria-label={t("myProfile.emblems.toggle")}
          >
            +
          </span>
        </h2>

        <p>{t("myProfile.emblems.description")}</p>

        <div className="my-profiles-rows">
          {CATEGORY_ORDER.map((categoryKey) => (
            <div key={categoryKey} className="my-profiles-row">
              <strong>{t(`myProfile.emblems.categories.${categoryKey}`)}</strong>

              <div className="my-profiles-icons">
                {(categorizedEmblems[categoryKey] || []).map((emblem) => {
                  const idx = (emblem.emblemId || 0) - 1;
                  const smallSrc = images1[idx];
                  const fullSrc = images[idx];

                  if (!smallSrc || !fullSrc) return null;

                  return (
                    <img
                      key={emblem.emblemId}
                      src={smallSrc}
                      alt={t("myProfile.emblems.emblemAlt", { id: emblem.emblemId })}
                      onClick={() => handleImageClick(fullSrc)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        className={`my-profiles-right ${view === "right" ? "expanded" : ""} ${
          view === "left" ? "hidden" : ""
        }`}
      >
        <h2>{t("myProfile.graphics.title")}</h2>

        {showStats ? (
          <div className="myprofile-stats">
            <div className="myprofile-block">
              <div className="myprofile-subtitle">{t("myProfile.stats.subRarity")}</div>
              <div className="myprofile-card">
                {rarityLoading ? (
                  <div className="myprofile-loading">{t("common.loading")}</div>
                ) : rarityError ? (
                  <div className="myprofile-error">
                    <div>{t("myProfile.rarity.error", { msg: String(rarityError) })}</div>
                    <button className="myprofile-retry" onClick={fetchRarity} type="button">
                      {t("common.retry")}
                    </button>
                  </div>
                ) : (
                  <RarityBar percent={rarityPct} />
                )}
              </div>
            </div>

            <div className="myprofile-block">
              <div className="myprofile-subtitle">{t("myProfile.stats.subTeamComposition")}</div>
              <div className="myprofile-card" style={{ height: 240 }}>
                <Bar data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>
        ) : (
          <div className="graphics-grid" />
        )}
      </div>
    </div>
  );
};

const RarityBar = ({ percent }) => {
  const pct = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  const label = `${Math.round(pct)} %`;

  return (
    <div className="rarity-wrap">
      <div className="rarity-bar" role="img" aria-label={label}>
        <div className="rarity-bar__track" />
        <div className="rarity-bar__fill" style={{ width: `${pct}%` }} />
        <div className="rarity-bar__label">{label}</div>
      </div>
    </div>
  );
};

export default MyProfile;
